.sidebar {
  display: flex;
  flex-direction: column;
  background-color: var(--tuna-palette-layers);
  border-right: solid 1px var(--tuna-palette-divider);
  color: var(--tuna-content-secondary);
  font-size: var(--tuna-typography-fontSize-sm);
  transition: width var(--tuna-transitions-duration-shorter, 200ms) var(--tuna-transitions-easing-easeInOut, ease);
  z-index: 1;
  width: 240px;
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
}

.sidebar--no-transition {
  transition: none;
}

.sidebar--collapsed {
  width: 64px;
}

/* ---- Header: wordmark + collapse toggle ---- */

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 0 10px 0 18px;
  flex-shrink: 0;
}

.sidebar--collapsed .header {
  padding: 0;
  justify-content: center;
}

.brand {
  font-size: var(--tuna-typography-fontSize-md);
  font-weight: 650;
  letter-spacing: -0.01em;
  color: var(--tuna-content-primary);
}

.iconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: var(--tuna-shape-radius-md);
  background: transparent;
  color: var(--tuna-content-secondary);
  cursor: pointer;
}

.iconButton:hover {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-content-primary);
}

/* ---- Search field ---- */

.search {
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  height: 34px;
  margin: 0 10px 6px;
  padding: 0 8px 0 10px;
  border: 1px solid var(--tuna-palette-divider);
  border-radius: var(--tuna-shape-radius-md);
  background: transparent;
  color: var(--tuna-content-secondary);
  font: inherit;
  text-align: left;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}

.search:hover,
.search--open {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-content-primary);
}

.sidebar--collapsed .search {
  width: 40px;
  margin: 0 12px 6px;
  padding: 0;
  justify-content: center;
  border-color: transparent;
}

/* ---- Scrolling nav ---- */

/* The nav scrolls on its own, between the header and the account footer. When the whole sidebar
   scrolled, rows slid under those controls and were left with a sliver of hit area (2.5.8). */
.nav {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 2px 10px 16px;
}

.sidebar--collapsed .nav {
  padding: 2px 12px 16px;
}

.row {
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  min-height: 32px;
  margin: 0;
  padding: 0 10px;
  border: 0;
  border-radius: var(--tuna-shape-radius-md);
  background: transparent;
  color: var(--tuna-content-secondary);
  font: inherit;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
}

.row:hover {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-content-primary);
}

.row--active {
  background-color: color-mix(in oklch, var(--tuna-palette-primary-main) 12%, transparent);
  color: var(--tuna-content-primary);
  font-weight: 550;
}

.row--active .icon {
  color: var(--tuna-palette-primary-main);
}

.sidebar--collapsed .row {
  width: 40px;
  padding: 0;
  justify-content: center;
}

.icon {
  display: inline-flex;
  flex-shrink: 0;
}

.icon svg {
  width: 18px;
  height: 18px;
}

.grow {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Collapsed rail: labels are visually hidden, not removed, so every row keeps its accessible name. */
.sidebar--collapsed .label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Focus ring for every interactive element in the sidebar, including the rows built by ToolsMenuItems. */
.sidebar button:focus-visible,
.resizeHandle:focus-visible {
  outline: 2px solid var(--tuna-focus-ring);
  outline-offset: -2px;
}

@media (pointer: coarse) {
  .row,
  .search {
    min-height: 44px;
  }
}

/* ---- Sections ---- */

.section {
  margin-top: 18px;
}

.sidebar--collapsed .section {
  margin-top: 0;
}

.sectionHeader {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 26px;
  margin: 0 0 2px;
  padding: 0 10px;
  border: 0;
  border-radius: var(--tuna-shape-radius-sm);
  background: transparent;
  color: var(--tuna-content-secondary);
  font-family: inherit;
  font-size: var(--tuna-typography-fontSize-xs);
  font-weight: 500;
  line-height: 1;
  text-align: left;
  cursor: pointer;
}

.sectionHeader:hover {
  color: var(--tuna-content-primary);
}

.sectionContent {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sectionContent[hidden] {
  display: none;
}

.sectionDivider {
  width: 24px;
  height: 1px;
  margin: 10px auto;
  background-color: var(--tuna-palette-divider);
}

.count {
  font-size: var(--tuna-typography-fontSize-xs);
  font-variant-numeric: tabular-nums;
}

.chevron {
  width: 6px;
  height: 6px;
  margin: 0 3px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg);
  transition: transform var(--tuna-transitions-duration-shortest, 150ms) ease;
}

.chevron--closed {
  transform: rotate(-45deg);
}

/* ---- Favorites ---- */

/* The entity colour shows at rest: the icon in the colour, on a tint of it. */
.tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: var(--tuna-shape-radius-sm);
  background-color: color-mix(in oklch, currentColor 14%, transparent); /* tuna-allow: tints the favorite's own color */
}

.tile svg {
  width: 13px;
  height: 13px;
}

.empty {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 2px 0 0;
  padding: 10px;
  border: 1px dashed var(--tuna-palette-divider);
  border-radius: var(--tuna-shape-radius-md);
  color: var(--tuna-content-secondary);
  font-size: var(--tuna-typography-fontSize-xs);
  line-height: 1.45;
  white-space: normal;
}

.empty .icon {
  margin-top: 2px;
}

.empty .icon svg {
  width: 14px;
  height: 14px;
}

.sidebar--collapsed .empty {
  display: none;
}

/* ---- Footer: account ---- */

.footer {
  flex-shrink: 0;
  border-top: 1px solid var(--tuna-palette-divider);
  padding: 8px 10px;
}

.sidebar--collapsed .footer {
  padding: 8px 12px;
}

.account {
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  border-radius: var(--tuna-shape-radius-md);
  background: transparent;
  color: var(--tuna-content-secondary);
  font: inherit;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
}

.account:hover {
  background-color: var(--tuna-palette-action-hover);
}

.sidebar--collapsed .account {
  width: 40px;
  padding: 6px 0;
  justify-content: center;
}

.avatar {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.pip {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 8px;
  height: 8px;
  border-radius: var(--tuna-shape-radius-circle);
  background-color: var(--tuna-palette-warning-main);
  box-shadow: 0 0 0 2px var(--tuna-palette-background-default);
}

.who {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tuna-content-primary);
  font-weight: 550;
}

.sync {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--tuna-typography-fontSize-xs);
}

.dot {
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: var(--tuna-shape-radius-circle);
  background-color: var(--tuna-content-secondary);
  opacity: 0.6;
}

.dot--pending {
  background-color: var(--tuna-palette-warning-main);
  opacity: 1;
}

/* ---- Resize handle ---- */

.resizeHandle {
  position: absolute;
  top: 0;
  right: -4px;
  width: 7px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
}

.resizeHandle::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 0;
  width: 2px;
  height: 100%;
}

.resizeHandle:hover::after,
.resizeHandle:active::after,
.resizeHandle:focus-visible::after {
  background-color: var(--tuna-palette-primary-main);
}

@media (prefers-reduced-motion: reduce) {
  .sidebar,
  .chevron {
    transition: none;
  }
}
